import { action, observable, computed, runInAction } from "mobx";
import { EventsRequestFilter } from "@api-types/events";
import { MappedEvent, ResultsFormattedData } from "@data-types/results";
import type { RootStore } from "@state/stores/types";
import ResultsService from "@services/results/ResultsService.mobile";
import ResultsMenuStoreMobile from "./ResultsMenuStoreMobile";

export default class ResultsDataStoreMobile {
	rootStore: RootStore;
	menuStore: ResultsMenuStoreMobile;

	bettingTypeIds: string[] | null = null;
	scoreChangeEventId: string | null = null;

	@observable resultData: ResultsFormattedData<MappedEvent[]>[] | null;
	@observable isSearch = false;
	@observable searchTerm = "";

	constructor(rootStore: RootStore, menuStore: ResultsMenuStoreMobile) {
		this.rootStore = rootStore;
		this.menuStore = menuStore;
	}

	@computed get totalEvents() {
		if (!this.resultData) return 0;

		return this.resultData.reduce((acc, v) => {
			v.tournaments.forEach((t) => t.events.forEach((ev) => acc++));
			return acc;
		}, 0);
	}

	@computed get filteredResultData() {
		if (!this.resultData || !this.resultData.length) return [];

		if (this.searchTerm === "") {
			return this.resultData;
		}

		return this.resultData
			.map((entry) => ({
				...entry,
				tournaments: entry.tournaments
					.map((t) => ({
						...t,
						events: t.events.filter(
							(ev) =>
								ev.teamOneName
									.toLowerCase()
									.includes(this.searchTerm.toLowerCase()) ||
								ev.teamTwoName
									.toLowerCase()
									.includes(this.searchTerm.toLowerCase())
						),
					}))
					.filter((t) => t.events.length),
			}))
			.filter((entry) => entry.tournaments.length);
	}

	@action.bound
	async onInitialize() {
		if (!this.bettingTypeIds) {
			this.bettingTypeIds = await ResultsService.getBettingTypeIds();
		}

		const selectedTournament = this.menuStore.selectedTournament;
		const selectedCategory = this.menuStore.selectedCategory;
		const searchString = this.rootStore.searchBarViewStore.searchTerm;

		this.isSearch =
			!!searchString && !selectedTournament && !selectedCategory;

		const requestFilter: EventsRequestFilter = {
			startDayFromToday: this.menuStore.defaultDaysFromToday,
			endDayFromToday: null,
			tournamentIds: selectedTournament ? [selectedTournament.id] : null,
			sportCategoryIds:
				!selectedTournament && selectedCategory
					? [selectedCategory.id]
					: null,
			searchPhrase: this.isSearch ? searchString.toLowerCase() : null,
		};

		const resultDataRequest = await ResultsService.getResults(
			requestFilter,
			this.bettingTypeIds
		);

		if (resultDataRequest.success) {
			runInAction(() => {
				this.resultData = resultDataRequest.data;
			});
		}
	}

	@action.bound
	async getEventAdditionalInfo(eventId: string) {
		if (!this.scoreChangeEventId) {
			this.scoreChangeEventId =
				await ResultsService.getScoreChangeEventId();
		}

		return ResultsService.getEventAdditionalInfo(
			eventId,
			this.scoreChangeEventId
		);
	}

	@action.bound
	async onReset() {
		this.searchTerm = "";
		this.resultData = null;
	}

	@action.bound
	onSearchTermChange(value: string) {
		this.searchTerm = value;
	}
}
